/**
 * artifactHistory — 대화/메시지에 저장된 학습지·PPT 산출물 레코드를 읽고, 레거시 필드만 있는 옛 대화를
 * 레코드 모양으로 바꾼다. (레코드 계약 자체는 artifactRecords.js)
 *
 *  - 대화 문서: worksheetArtifacts[] / pptArtifacts[] (최신 우선, 최대 20)
 *  - 레거시: worksheetDownloadUrl·worksheetFilename·worksheetPreviewUrls / pptDownloadUrl·pptFilename·pptPreviewUrls·pptDownloadMode
 *  - 메시지: artifacts[] 에는 채팅 파일 카드(file_id)와 산출물 레코드(kind/format)가 섞여 있을 수 있다.
 */

import { ARTIFACT_FORMATS, buildArtifactRecord, isArtifactRecord } from './artifactRecords';

export const ARTIFACT_FIELD_BY_CATEGORY = Object.freeze({
  worksheet_gen: 'worksheetArtifacts',
  ppt_maker: 'pptArtifacts',
});

export const LEGACY_ARTIFACT_ID = Object.freeze({
  worksheet_gen: 'legacy_worksheet_1',
  ppt_maker: 'legacy_ppt_1',
});

const sortLatestFirst = (records) => (
  [...records].sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')))
);

const formatFromFilename = (filename, fallback) => {
  const match = /\.([A-Za-z0-9]+)$/.exec(String(filename || ''));
  const ext = match ? match[1].toLowerCase() : '';
  return ARTIFACT_FORMATS.has(ext) ? ext : fallback;
};

const toIso = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString();
  if (typeof value.toDate === 'function') {
    try { return value.toDate().toISOString(); } catch (_e) { return null; }
  }
  if (typeof value === 'number' && Number.isFinite(value)) return new Date(value).toISOString();
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000).toISOString();
  return null;
};

const cleanUrls = (urls) => (Array.isArray(urls) ? urls.filter((u) => typeof u === 'string' && u.trim()) : []);

/**
 * 레거시 필드(worksheetDownloadUrl 등)만 있는 대화를 레코드 1개로 바꾼다. 없으면 null.
 */
export function legacyArtifactRecord(conversation, category) {
  if (!conversation || typeof conversation !== 'object') return null;
  const createdAt = toIso(conversation.updatedAt) || toIso(conversation.createdAt) || new Date(0).toISOString();
  try {
    if (category === 'ppt_maker') {
      if (!conversation.pptDownloadUrl) return null;
      const previewUrls = cleanUrls(conversation.pptPreviewUrls);
      const originalCount = cleanUrls(conversation.pptOriginalUrls).length;
      const sourceMode = conversation.pptDownloadMode === 'text'
        ? 'text'
        : (conversation.pptDownloadMode === 'image' || previewUrls.length > 1 || originalCount > 1 ? 'image' : 'text');
      const slideCount = Array.isArray(conversation.pptOutline?.slides) ? conversation.pptOutline.slides.length : 0;
      return buildArtifactRecord({
        id: LEGACY_ARTIFACT_ID.ppt_maker,
        kind: 'ppt',
        format: formatFromFilename(conversation.pptFilename, 'pptx'),
        filename: conversation.pptFilename || 'PPT.pptx',
        downloadUrl: conversation.pptDownloadUrl,
        pageCount: sourceMode === 'image' ? (previewUrls.length || originalCount || slideCount) : (slideCount || previewUrls.length || 1),
        previewUrls,
        sourceMode,
        createdAt,
      });
    }
    if (!conversation.worksheetDownloadUrl) return null;
    const previewUrls = cleanUrls(conversation.worksheetPreviewUrls);
    const originalCount = cleanUrls(conversation.worksheetOriginalUrls).length;
    const canvasPages = Array.isArray(conversation.worksheetCanvas?.slides) ? conversation.worksheetCanvas.slides.length : 0;
    // 텍스트(캔버스) 학습지는 썸네일이 1장(첫 쪽)만 남는다. 원본 이미지가 여러 장이면 그림 학습지.
    const sourceMode = originalCount > 0 || previewUrls.length > 1 ? 'image' : 'text';
    return buildArtifactRecord({
      id: LEGACY_ARTIFACT_ID.worksheet_gen,
      kind: 'worksheet',
      format: formatFromFilename(conversation.worksheetFilename, 'pdf'),
      filename: conversation.worksheetFilename || '학습지.pdf',
      downloadUrl: conversation.worksheetDownloadUrl,
      pageCount: sourceMode === 'image' ? (previewUrls.length || originalCount) : (canvasPages || previewUrls.length || 1),
      previewUrls,
      sourceMode,
      createdAt,
    });
  } catch (_e) {
    return null;
  }
}

/**
 * 대화 문서에서 산출물 레코드 목록(최신 우선). 레코드 필드가 비어 있으면 레거시 필드를 변환해 1개를 돌려준다.
 */
export function artifactRecordsFromConversation(conversation, category) {
  if (!conversation || typeof conversation !== 'object') return [];
  const field = ARTIFACT_FIELD_BY_CATEGORY[category];
  const stored = field && Array.isArray(conversation[field]) ? conversation[field].filter(isArtifactRecord) : [];
  if (stored.length > 0) return sortLatestFirst(stored);
  const legacy = legacyArtifactRecord(conversation, category);
  return legacy ? [legacy] : [];
}

/** 메시지 artifacts[] 를 채팅 파일 카드와 산출물 레코드로 나눈다. */
export function splitMessageArtifacts(message) {
  const list = Array.isArray(message?.artifacts) ? message.artifacts : [];
  const records = [];
  const fileArtifacts = [];
  list.forEach((item) => {
    if (isArtifactRecord(item)) records.push(item);
    else if (item) fileArtifacts.push(item);
  });
  return { fileArtifacts, records };
}

/** 카드가 받는 포맷 줄. DOCX/HWPX는 내보내기 배포 플래그가 켜져야 열린다. */
export function availableFormatsFor(kind, { exportEnabled = false, includePptPdf = false } = {}) {
  if (kind === 'ppt') return includePptPdf ? ['pptx', 'pdf'] : ['pptx'];
  return exportEnabled ? ['hwpx', 'pdf', 'docx'] : ['pdf'];
}

export function isWorksheetExportEnabled(env = process.env) {
  const raw = String(env.REACT_APP_WS_EXPORT_ENABLED ?? '').trim().toLowerCase();
  return !['false', '0', 'off'].includes(raw);
}

const artifactHistory = {
  ARTIFACT_FIELD_BY_CATEGORY,
  LEGACY_ARTIFACT_ID,
  artifactRecordsFromConversation,
  availableFormatsFor,
  isWorksheetExportEnabled,
  legacyArtifactRecord,
  splitMessageArtifacts,
};

export default artifactHistory;
